import { useEffect, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { MdContentCopy } from "react-icons/md";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";

const COPIED_RESET_MS = 1000;

export interface CopyLinkButtonProps {
  /** Null keeps the button rendered but inert, matching the Open button beside it. */
  value: string | null;
  label?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * The hub's one copy-a-link affordance. It mirrors TextCopy's confirmation so both copy gestures
 * behave alike, rather than MeritoButton's `loading` — that spinner is white and `tertiary` is
 * transparent, so it would be invisible here.
 */
export const CopyLinkButton = ({
  value,
  label = "Copy Link",
  disabled = false,
  className = "",
}: CopyLinkButtonProps): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const copy = async (): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      setCopied(false);
    }
  };

  return (
    <MeritoButton
      type="tertiary"
      size="md"
      className={className}
      icon={copied ? <FaCheck aria-hidden="true" /> : <MdContentCopy aria-hidden="true" />}
      btnText={label}
      disabled={!value || disabled}
      onClick={() => { void copy(); }}
    />
  );
};

export default CopyLinkButton;
